import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createInitialUserProfile, trackUserActivity } from '../services/userProfileService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('user'); // 'user', 'provider', 'admin'
  const [loading, setLoading] = useState(true);

  // Role detection function
  const detectUserRole = useCallback(async (user) => {
    if (!user) {
      setUserRole('user');
      return 'user';
    }

    try {
      // Check if user is admin (you can define admin emails)
      const adminEmails = ['admin@solarinsight.co.za', 'support@solarinsight.co.za'];
      if (adminEmails.includes(user.email)) {
        setUserRole('admin');
        return 'admin';
      }

      // Check if user is a provider
      const providerDoc = await getDoc(doc(db, 'providers', user.uid));
      if (providerDoc.exists()) {
        const providerData = providerDoc.data();
        // Consider user a provider if they have a provider document, regardless of status
        if (providerData.status === 'approved' || providerData.status === 'pending_review' || providerData.status === 'draft') {
          setUserRole('provider');
          return 'provider';
        }
      }

      // Check user profile for role field
      const userProfileDoc = await getDoc(doc(db, 'userProfiles', user.uid));
      if (userProfileDoc.exists() && userProfileDoc.data().role) {
        const role = userProfileDoc.data().role;
        setUserRole(role);
        return role;
      }

      // Default to regular user
      setUserRole('user');
      return 'user';
    } catch (error) {
      console.error('Error detecting user role:', error);
      setUserRole('user');
      return 'user';
    }
  }, []);

  const signup = useCallback(async (email, password, name) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, {
        displayName: name
      });
      
      // Create user profile in Firestore
      try {
        await createInitialUserProfile(result.user);
      } catch (profileError) {
        console.error('Error creating user profile:', profileError);
        // Don't fail signup if profile creation fails
      }
      
      return result;
    } catch (error) {
      throw new Error(getErrorMessage(error.code));
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Track user activity
      try {
        await trackUserActivity(result.user.uid, 'login');
      } catch (activityError) {
        console.error('Error tracking user activity:', activityError);
        // Don't fail login if activity tracking fails
      }
      
      return result;
    } catch (error) {
      throw new Error(getErrorMessage(error.code));
    }
  }, []);

  const logout = useCallback(() => {
    setUserRole('user');
    return signOut(auth);
  }, []);

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password';
      default:
        return 'An error occurred during authentication';
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await detectUserRole(user);
      } else {
        setUserRole('user');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [detectUserRole]);

  // Helper functions for role checking
  const isAdmin = useMemo(() => userRole === 'admin', [userRole]);
  const isProvider = useMemo(() => userRole === 'provider', [userRole]);
  const isUser = useMemo(() => userRole === 'user', [userRole]);

  const value = useMemo(() => ({
    currentUser,
    userRole,
    isAdmin,
    isProvider,
    isUser,
    signup,
    login,
    logout,
    loading,
  }), [currentUser, userRole, isAdmin, isProvider, isUser, signup, login, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};